'use client';

import React, { FC } from 'react';
import { useParams } from 'next/navigation';
import Slider from 'components/commercetools-ui/atoms/slider';
import Typography from 'components/commercetools-ui/atoms/typography';
import { mapProduct } from 'helpers/algolia/map-product';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop, minitablet, mobile, smallMobile, tablet } from 'helpers/utils/screensizes';
import ProductTile from '../product-tile';

export interface RelatedProductsSliderProps {
  title: string;
}

/**
 * PDP "Related products" slider (server path). Renders server-fetched Algolia hits, already in display
 * order, so the tiles are in the SSR HTML. The ids `related-products` / `related-products-title` are read
 * by the PDP sidebar (product-details/components/sidebar.tsx).
 */
const RelatedProductsSlider: FC<RelatedProductsSliderProps & { hits: any[] }> = ({ hits, title }) => {
  const [isMiniTabletSize] = useMediaQuery(minitablet);
  const [isTabletSize] = useMediaQuery(tablet);
  const [isDesktopSize] = useMediaQuery(desktop);
  const { locale } = useParams();

  return (
    <div className="pt-22 md:pt-22 pb-48 md:pb-48 lg:py-48 xl:py-70 2xl:py-70 3xl:py-103 lg:grid lg:grid-cols-12 pr-0 md:pb-1 bg-[#F3EDE9] w-screen relative left-full -translate-x-full">
      <div id="related-products" className="lg:col-span-9 lg:col-start-4 pt-26 pl-24 md:pl-28 lg:pl-30 xl:pl-0 2xl:pl-0 xl:py-60">
        <div>
          <Typography
            id="related-products-title"
            className="pb-32 text-32 font-medium text-primary-black xl:text-36 2xl:text-48"
            as="h3"
          >
            {title}
          </Typography>
        </div>
        <div>
          <div className="product-slider-container relative w-full">
            <Slider
              className="mb-48"
              slidesPerView={2.1}
              slidesPerGroup={1}
              dots={false}
              autoplay={false}
              arrows={isTabletSize || isDesktopSize || isMiniTabletSize}
              innerArrows
              solidArrows
              allowArrowsOnTouchDevice
              nextButtonStyles={{ top: '100%', left: 50 }}
              prevButtonStyles={{ top: '100%', left: 0 }}
              allowTouchMove
              spaceBetween={8}
              breakpoints={{
                [tablet]: { slidesPerView: 3.25, slidesPerGroup: 3, spaceBetween: 16 },
                [smallMobile]: { slidesPerView: 1.2, slidesPerGroup: 1, spaceBetween: 16 },
                [mobile]: { slidesPerView: 2.1, slidesPerGroup: 2, spaceBetween: 16 },
              }}
            >
              {hits.map((hit: any) => (
                <div key={hit.id || hit.objectID} className="mb-48">
                  <ProductTile product={mapProduct(hit, locale)} layout="grid" />
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RelatedProductsSlider;
